/**
 * React Query hooks for Leads automation rules.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, {
  LeadsAutomationExecution,
  LeadsAutomationRule,
  LeadsCreateAutomationInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useAutomations() {
  const queryClient = useQueryClient();

  const query = useQuery<{ rules: LeadsAutomationRule[] }>({
    queryKey: queryKeys.leads.automations.all(),
    queryFn: () => api.listLeadsAutomations(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.leads.automations.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateAutomationInput) => api.createLeadsAutomation(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create automation', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeadsCreateAutomationInput> }) =>
      api.updateLeadsAutomation(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update automation', variant: 'destructive' }),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.toggleLeadsAutomation(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to toggle automation', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsAutomation(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete automation', variant: 'destructive' }),
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load automations', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    rules: query.data?.rules ?? [],
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createRule: (input: LeadsCreateAutomationInput) => createMutation.mutateAsync(input),
    updateRule: (id: string, input: Partial<LeadsCreateAutomationInput>) =>
      updateMutation.mutateAsync({ id, input }),
    toggleRule: (id: string) => toggleMutation.mutateAsync(id),
    deleteRule: (id: string) => deleteMutation.mutateAsync(id),
    getExecutions: async (id: string): Promise<LeadsAutomationExecution[]> => {
      const result = await api.getLeadsAutomationExecutions(id);
      return result.executions;
    },
  };
}
